import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AdminLayout } from '../../components/Layout';
import { ArrowLeft, Edit2, Eye, EyeOff, Video, Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSeriesDetail() {
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const [series, setSeries] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [seriesId]);

  const fetchData = async () => {
    try {
      const [seriesRes, videosRes] = await Promise.all([
        axios.get(`${API}/series/${seriesId}`),
        axios.get(`${API}/videos/series/${seriesId}`)
      ]);
      setSeries(seriesRes.data);
      setVideos(videosRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleVideoPublished = async (video) => {
    try {
      await axios.put(`${API}/videos/${video.id}`, {
        ...video,
        published: !video.published
      });
      toast.success(`Video ${video.published ? 'unpublished' : 'published'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update video');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-edupaws-sage border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!series) {
    return (
      <AdminLayout>
        <div className="text-center py-16">
          <h2 className="font-heading text-2xl font-bold text-gray-800 mb-4">
            Series Not Found
          </h2>
          <Link to="/admin/series" className="text-edupaws-sage font-body">
            Back to Content
          </Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Back Button */}
      <button
        onClick={() => navigate('/admin/series')}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 font-body"
        data-testid="back-to-series-list"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Content
      </button>

      {/* Series Header */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm mb-8">
        <div className="flex items-start justify-between">
          <div>
            <span className="inline-block bg-edupaws-sage/20 text-edupaws-navy px-3 py-1 rounded-full text-sm font-body font-medium mb-3">
              Series {series.order_index}
            </span>
            <h1 className="font-heading text-2xl font-bold text-gray-800 mb-2">
              {series.title}
            </h1>
            <p className="text-gray-500 font-body max-w-2xl">
              {series.description}
            </p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-body font-medium
            ${series.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
            {series.published ? 'Published' : 'Draft'}
          </span>
        </div>
      </div>

      {/* Videos Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-gray-800">
            Videos ({videos.length})
          </h2>
          {videos.length < 1 && (
            <Link
              to={`/admin/video/new/edit?series=${seriesId}`}
              className="flex items-center gap-2 font-body font-medium text-sm px-4 py-2 bg-edupaws-navy text-white rounded-lg hover:bg-edupaws-navy/90 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Video
            </Link>
          )}
        </div>

        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left py-3 px-6 font-body font-medium text-gray-600 text-sm">#</th>
              <th className="text-left py-3 px-6 font-body font-medium text-gray-600 text-sm">Title</th>
              <th className="text-left py-3 px-6 font-body font-medium text-gray-600 text-sm">Goal / Outcome</th>
              <th className="text-left py-3 px-6 font-body font-medium text-gray-600 text-sm">Watch URL</th>
              <th className="text-left py-3 px-6 font-body font-medium text-gray-600 text-sm">Status</th>
              <th className="text-right py-3 px-6 font-body font-medium text-gray-600 text-sm">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {videos.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-12 text-center">
                  <Video className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 font-body">No videos yet</p>
                </td>
              </tr>
            ) : (
              videos.map((video) => (
                <tr key={video.id} className="hover:bg-gray-50 transition-colors">
                  <td className="py-4 px-6">
                    <span className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center font-heading font-bold text-gray-600">
                      {video.order_index}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <span className="font-body font-medium text-gray-800 line-clamp-1">
                      {video.title}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <span className="text-sm text-gray-500 font-body line-clamp-1 max-w-xs">
                      {video.goal_outcome}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <a 
                      href={video.watch_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-edupaws-sage hover:text-edupaws-navy font-body truncate block max-w-[200px]"
                    >
                      {video.watch_url}
                    </a>
                  </td>
                  <td className="py-4 px-6">
                    <button
                      onClick={() => toggleVideoPublished(video)}
                      data-testid={`toggle-video-${video.order_index}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-body font-medium cursor-pointer
                        ${video.published ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                    >
                      {video.published ? (
                        <>
                          <Eye className="w-3 h-3" /> Published
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-3 h-3" /> Draft
                        </>
                      )}
                    </button>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center justify-end">
                      <Link
                        to={`/admin/video/${video.id}/edit`}
                        data-testid={`edit-video-${video.order_index}`}
                        className="p-2 hover:bg-edupaws-sage/10 rounded-lg transition-colors text-gray-500 hover:text-edupaws-navy"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
